import constants from "constants";

const { logoutInProgress, postLogoutPublicNav } =
  constants.sessionStorageKeys;

/** @returns {boolean} */
export function isLogoutInProgress() {
  return sessionStorage.getItem(logoutInProgress) === "1";
}

export function setLogoutSessionFlags() {
  sessionStorage.setItem(logoutInProgress, "1");
  sessionStorage.setItem(postLogoutPublicNav, "1");
}

export function clearLogoutSessionFlags() {
  sessionStorage.removeItem(logoutInProgress);
  sessionStorage.removeItem(postLogoutPublicNav);
}

/**
 * True only for B2C logout round-trips — not when a login response includes an id token.
 * @param {import("@azure/msal-browser").AuthenticationResult | null} [response]
 * @returns {boolean}
 */
export function isReturningFromLogoutResponse(response) {
  return isLogoutInProgress() && !response?.idToken;
}

/**
 * Clears app auth storage while preserving device_id and optional session cache keys.
 * @param {{ sessionCacheKeysToPreserve?: string[] }} [options]
 */
export function clearAppAuthStorage(options = {}) {
  const { sessionCacheKeysToPreserve = [] } = options;
  const deviceId = localStorage.getItem("device_id");
  const preservedCache = {};

  sessionCacheKeysToPreserve.forEach((key) => {
    const value = sessionStorage.getItem(key);
    if (value) {
      preservedCache[key] = value;
    }
  });

  setLogoutSessionFlags();
  localStorage.clear();
  sessionStorage.clear();

  if (deviceId) {
    localStorage.setItem("device_id", deviceId);
  }

  Object.entries(preservedCache).forEach(([key, value]) => {
    sessionStorage.setItem(key, value);
  });

  setLogoutSessionFlags();
}

/**
 * Clear local auth state and redirect to B2C logout (single navigation back to `/`).
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @param {{ sessionCacheKeysToPreserve?: string[] }} [options]
 */
export function finalizeAppLogoutRedirect(instance, options = {}) {
  clearAppAuthStorage(options);
  return instance.logoutRedirect({
    postLogoutRedirectUri: window.location.origin,
  });
}

/**
 * Remove MSAL redirect query/hash fragments left after handleRedirectPromise.
 */
export function cleanMsalRedirectUrl() {
  if (typeof window === "undefined") {
    return;
  }

  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(search);
  const msalQueryKeys = [
    "state",
    "code",
    "session_state",
    "error",
    "error_description",
  ];
  const hasMsalQuery = msalQueryKeys.some((key) => params.has(key));
  const hasMsalHash = /[#&](state|code|error)=/i.test(hash);

  if (!hasMsalQuery && !hasMsalHash) {
    return;
  }

  msalQueryKeys.forEach((key) => params.delete(key));
  const nextSearch = params.toString();
  const nextUrl =
    pathname + (nextSearch ? `?${nextSearch}` : "");
  window.history.replaceState(window.history.state, "", nextUrl);
}
